import { createSlice } from "@reduxjs/toolkit";

const DEFAULT_PAGE_SIZE = 10;

const createInitialDataState = (pageSize = DEFAULT_PAGE_SIZE) => ({
  onPage: 1,
  pageSize,
  totalPages: 0,
  totalRecords: 0,
  data: [],
});

const resetDataState = (pageSize = DEFAULT_PAGE_SIZE) => ({
  onPage: 1,
  pageSize,
  totalPages: 0,
  totalRecords: 0,
  data: [],
});

const updateDataState = (
  state,
  details
) => {
  if (!details) {
    return state;
  }
  
  const totalRecords = Array.isArray(details.data) ? details?.totalRecords : 0;
  const totalPages = Array.isArray(details.data) ? details?.totalPages : 0;

  return {
    ...state,
    onPage: details.onPage,
    pageSize: details.pageSize,
    totalPages,
    totalRecords,
    data: details.data || [],
  };
};

const drmLiveLoadMasterDataSlice = createSlice({
  name: "masterData",
  initialState: {
    masterData: {
      countries: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      divisions: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      zones: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      cities: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      states: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      carriers: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      services: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      transports: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      equipments: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      charges: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      tariffIds: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      tariffEquipmentMapping: { ...createInitialDataState(), isLoading: false, errorMessage: undefined }, 
      zoneShortDesc: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
      zoneLongDesc: { ...createInitialDataState(), isLoading: false, errorMessage: undefined },
    },
    globalLoading: false,
    globalError: undefined,
    lastFetchType: undefined,
    lastFetchTimestamp: undefined,
  },
  reducers: {
    startMasterDataFetching: (state, action) => {
      const { masterDataType = "all" } = action.payload || {};
      state.globalLoading = true;
      state.globalError = undefined;
      state.lastFetchType = masterDataType;

      if (masterDataType === "all") {
        Object.keys(state.masterData).forEach((key) => {
          state.masterData[key].isLoading = true;
          state.masterData[key].errorMessage = undefined;
        });
      } else if (state.masterData[masterDataType]) {
        state.masterData[masterDataType].isLoading = true;
        state.masterData[masterDataType].errorMessage = undefined;
      }
    },

    setMasterDataSuccess: (state, action) => {
      const { data, stats } = action.payload || {};
      state.globalLoading = false;
      state.globalError = undefined;
      state.lastFetchTimestamp = new Date().toISOString();
      if (data && typeof data === "object") {
        Object.keys(data).forEach((key) => {
          const incomingData = data[key];
          if (state.masterData[key] && Array.isArray(incomingData.data)) {
            state.masterData[key] = {
              ...state.masterData[key],
              ...updateDataState(state.masterData[key], incomingData),
              isLoading: false,
              errorMessage: undefined,
            };
          }
        });
      }
      if (stats) {
        state.lastFetchStats = stats;
      }
    },

    setMasterDataFailure: (state, action) => {
      const { message, apiCallType, affectedEndpoints } = action.payload || {};
      state.globalLoading = false;
      state.globalError = message || action.payload;

      if (apiCallType === "all" || !apiCallType) {
        Object.keys(state.masterData).forEach((key) => {
          state.masterData[key] = {
            ...resetDataState(),
            isLoading: false,
            errorMessage: message || "Failed to fetch data",
          };
        });
      } else if (affectedEndpoints && Array.isArray(affectedEndpoints)) {
        affectedEndpoints.forEach((endpoint) => {
          if (state.masterData[endpoint]) {
            state.masterData[endpoint] = {
              ...resetDataState(),
              isLoading: false,
              errorMessage: message || "Failed to fetch data",
            };
          }
        });
      } else if (state.masterData[apiCallType]) {
        state.masterData[apiCallType] = {
          ...resetDataState(),
          isLoading: false,
          errorMessage: message || "Failed to fetch data",
        };
      }
    },

    // Countries
    setCountriesData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.countries = {
        ...state.masterData.countries,
        ...updateDataState(state.masterData.countries, details),
        isLoading: false,
        errorMessage: undefined,
      };
      
    },
    setCountriesLoading: (state, action) => {
      state.masterData.countries.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.countries.errorMessage = undefined;
    },
    setCountriesError: (state, action) => {
      state.masterData.countries.isLoading = false;
      state.masterData.countries.errorMessage = action.payload;
    },

    // Divisions
    setDivisionsData: (state, action) => {
       const { details } = action.payload || {};
      state.masterData.divisions = {
        ...state.masterData.divisions,
        ...updateDataState(state.masterData.divisions, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setDivisionsLoading: (state, action) => {
      state.masterData.divisions.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.divisions.errorMessage = undefined;
    },
    setDivisionsError: (state, action) => {
      state.masterData.divisions.isLoading = false;
      state.masterData.divisions.errorMessage = action.payload;
    },

    // Zones
    setZonesData: (state, action) => {
       const { details } = action.payload || {};
      state.masterData.zones = {
        ...state.masterData.zones,
        ...updateDataState(state.masterData.zones, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setZonesLoading: (state, action) => {
      state.masterData.zones.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.zones.errorMessage = undefined;
    },
    setZonesError: (state, action) => {
      state.masterData.zones.isLoading = false;
      state.masterData.zones.errorMessage = action.payload;
    },

    //Zone Short Description
    setZoneShortDescData: (state, action) => {
       const { details } = action.payload || {};
      state.masterData.zoneShortDesc = {
        ...state.masterData.zoneShortDesc,
        ...updateDataState(state.masterData.zoneShortDesc, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },

    setZoneShortDescLoading: (state, action) => {
      state.masterData.zoneShortDesc.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.zoneShortDesc.errorMessage = undefined;
    },

    setZoneShortDescError: (state, action) => {
      state.masterData.zoneShortDesc.isLoading = false;
      state.masterData.zoneShortDesc.errorMessage = action.payload;
    },

    //Zone Long Description
    setZoneLongDescData: (state, action) => {
        const { details } = action.payload || {};
      state.masterData.zoneLongDesc = {
        ...state.masterData.zoneLongDesc,
        ...updateDataState(state.masterData.zoneLongDesc, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },

    setZoneLongDescLoading: (state, action) => {
      state.masterData.zoneLongDesc.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.zoneLongDesc.errorMessage = undefined;
    },

    setZoneLongDescError: (state, action) => {
      state.masterData.zoneLongDesc.isLoading = false;
      state.masterData.zoneLongDesc.errorMessage = action.payload;
    },

    // Cities
    setCitiesData: (state, action) => {
       const { details } = action.payload || {};
      state.masterData.cities = {
        ...state.masterData.cities,
        ...updateDataState(state.masterData.cities, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setCitiesLoading: (state, action) => {
      state.masterData.cities.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.cities.errorMessage = undefined;
    },
    setCitiesError: (state, action) => {
      state.masterData.cities.isLoading = false;
      state.masterData.cities.errorMessage = action.payload;
    },

    // States
    setStatesData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.states = {
        ...state.masterData.states,
        ...updateDataState(state.masterData.states, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setStatesLoading: (state, action) => {
      state.masterData.states.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.states.errorMessage = undefined;
    },
    setStatesError: (state, action) => {
      state.masterData.states.isLoading = false;
      state.masterData.states.errorMessage = action.payload;
    },

    // Carriers
    setCarriersData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.carriers = {
        ...state.masterData.carriers,
        ...updateDataState(state.masterData.carriers, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setCarriersLoading: (state, action) => {
      state.masterData.carriers.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.carriers.errorMessage = undefined;
    },
    setCarriersError: (state, action) => {
      state.masterData.carriers.isLoading = false;
      state.masterData.carriers.errorMessage = action.payload;
    },

    // Services
    setServicesData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.services = {
        ...state.masterData.services,
        ...updateDataState(state.masterData.services, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setServicesLoading: (state, action) => {
      state.masterData.services.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.services.errorMessage = undefined;
    },
    setServicesError: (state, action) => {
      state.masterData.services.isLoading = false;
      state.masterData.services.errorMessage = action.payload;
    },

    // Transports
    setTransportsData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.transports = {
        ...state.masterData.transports,
        ...updateDataState(state.masterData.transports, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setTransportsLoading: (state, action) => {
      state.masterData.transports.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.transports.errorMessage = undefined;
    },
    setTransportsError: (state, action) => {
      state.masterData.transports.isLoading = false;
      state.masterData.transports.errorMessage = action.payload;
    },

    // Equipments
    setEquipmentsData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.equipments = {
        ...state.masterData.equipments,
        ...updateDataState(state.masterData.equipments, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setEquipmentsLoading: (state, action) => {
      state.masterData.equipments.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.equipments.errorMessage = undefined;
    },
    setEquipmentsError: (state, action) => {
      state.masterData.equipments.isLoading = false;
      state.masterData.equipments.errorMessage = action.payload;
    },

    // Charges
    setChargesData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.charges = {
        ...state.masterData.charges,
        ...updateDataState(state.masterData.charges, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setChargesLoading: (state, action) => {
      state.masterData.charges.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.charges.errorMessage = undefined;
    },
    setChargesError: (state, action) => {
      state.masterData.charges.isLoading = false;
      state.masterData.charges.errorMessage = action.payload;
    },
    //Tariff
    setTariffIdsData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.tariffIds = {
        ...state.masterData.tariffIds,
        ...updateDataState(state.masterData.tariffIds, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setTariffIdLoading: (state, action) => {
      state.masterData.tariffIds.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.tariffIds.errorMessage = undefined;
    },
    setTariffIdError: (state, action) => {
      state.masterData.tariffIds.isLoading = false;
      state.masterData.tariffIds.errorMessage = action.payload;
    },
    // Tariff Equipment Mapping
    setTariffEquipmentMappingData: (state, action) => {
      const { details } = action.payload || {};
      state.masterData.tariffEquipmentMapping = {
        ...state.masterData.tariffEquipmentMapping,
        ...updateDataState(state.masterData.tariffEquipmentMapping, details),
        isLoading: false,
        errorMessage: undefined,
      };
    },
    setTariffEquipmentMappingLoading: (state, action) => {
      state.masterData.tariffEquipmentMapping.isLoading = action.payload;
      state.globalLoading = false;
      if (action.payload) state.masterData.tariffEquipmentMapping.errorMessage = undefined;
    },
    setTariffEquipmentMappingError: (state, action) => {
      state.masterData.tariffEquipmentMapping.isLoading = false;
      state.masterData.tariffEquipmentMapping.errorMessage = action.payload;
    },

    // Utility actions
    clearAllData: (state) => {
      Object.keys(state.masterData).forEach((key) => {
        state.masterData[key] = {
          ...resetDataState(),
          isLoading: false,
          errorMessage: undefined,
        };
      });
      state.globalLoading = false;
      state.globalError = undefined;
    },

    clearSpecificData: (state, action) => {
      const { endpoint } = action.payload || {};
      if (endpoint && state.masterData[endpoint]) {
        state.masterData[endpoint] = {
          ...resetDataState(),
          isLoading: false,
          errorMessage: undefined,
        };
      }
    },

    setPageSize: (state, action) => {
      const { endpoint, pageSize } = action.payload || {};
      if (endpoint && state.masterData[endpoint]) {
        state.masterData[endpoint] = {
          ...state.masterData[endpoint],
          pageSize: pageSize,
          onPage: 1,
        };
      }
    },

    setCurrentPage: (state, action) => {
      const { endpoint, page } = action.payload || {};
      if (
        endpoint &&
        state.masterData[endpoint] &&
        page > 0 &&
        page <= state.masterData[endpoint].totalPages
      ) {
        state.masterData[endpoint].onPage = page;
      }
    },

    setPartialMasterDataFailure: (state, action) => {
      const { errors, affectedEndpoints } = action.payload || {};
      if (affectedEndpoints && Array.isArray(affectedEndpoints)) {
        affectedEndpoints.forEach((endpoint) => {
          if (state.masterData[endpoint]) {
            state.masterData[endpoint].isLoading = false;
            state.masterData[endpoint].errorMessage =
              errors?.find((e) => e.endpoint === endpoint)?.message ||
              "Failed to fetch data";
          }
        });
      }
      state.globalLoading = false;
      state.partialErrors = errors;
    },
  },
});

export const {
  startMasterDataFetching,
  setMasterDataSuccess,
  setMasterDataFailure,
  setPartialMasterDataFailure,
  setCountriesData,
  setCountriesLoading,
  setCountriesError,
  setDivisionsData,
  setDivisionsLoading,
  setDivisionsError,
  setZonesData,
  setZonesLoading,
  setZonesError,
  setZoneShortDescData,
  setZoneShortDescLoading,
  setZoneShortDescError,
  setZoneLongDescData,
  setZoneLongDescLoading,
  setZoneLongDescError,
  setCitiesData,
  setCitiesLoading,
  setCitiesError,
  setStatesData,
  setStatesLoading,
  setStatesError,
  setCarriersData,
  setCarriersLoading,
  setCarriersError,
  setServicesData,
  setServicesLoading,
  setServicesError,
  setTransportsData,
  setTransportsLoading,
  setTransportsError,
  setEquipmentsData,
  setEquipmentsLoading,
  setEquipmentsError,
  setTariffEquipmentMappingData,
  setTariffEquipmentMappingLoading,
  setTariffEquipmentMappingError,
  setChargesData,
  setChargesLoading,
  setChargesError,
  setTariffIdsData,
  setTariffIdLoading,
  setTariffIdError,
  clearAllData,
  clearSpecificData,
  setPageSize,
  setCurrentPage,
} = drmLiveLoadMasterDataSlice.actions;

export default drmLiveLoadMasterDataSlice.reducer;